import React, { useState, useEffect } from "react";
import { variantService } from "../pages/services/variantService";
import LoadingSpinner from "../common/LoadingSpinner";

const SampleNavigator = ({ token, onSamplesChanged }) => {
  const [sampleInfo, setSampleInfo] = useState(null);
  const [windowSize, setWindowSize] = useState(6);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial sample info
  useEffect(() => {
    const fetchSampleInfo = async () => {
      try {
        setIsLoading(true);
        const response = await variantService.getSampleInfo(token);
        const info = response.data;
        setSampleInfo(info);
        setWindowSize(info.window_size);
      } catch (error) {
        console.error("Error fetching sample info:", error);
        // Fallback to prevent crash if API fails
        setSampleInfo({
          current_sample_low: 0,
          current_sample_high: 0,
          window_size: 0,
          total_samples: 0,
          can_go_previous: false,
          can_go_next: false,
          current_file: "No File Selected"
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchSampleInfo();
  }, [token]);

  // Don't render until we have sample info
  if (!sampleInfo) {
    return (
      <div className="sample-navigator ag-grid-toolbar">
        <div className="d-flex align-items-center justify-content-center">
          <LoadingSpinner size="1em" className="me-2" />
          Loading sample information...
        </div>
      </div>
    );
  }

  const handleNavigation = async (action, params = {}) => {
    setIsLoading(true);
    try {
      let result;
      switch (action) {
        case "next":
          result = await variantService.nextSamples(token);
          break;
        case "previous":
          result = await variantService.previousSamples(token);
          break;
        case "setWindow":
          result = await variantService.setSampleWindow(
            token,
            params.windowSize
          );
          break;
        case "navigate":
          result = await variantService.navigateSamples(
            token,
            params.sample_low,
            params.sample_high
          );
          break;
        default:
          return;
      }

      const info = result.data;
      setSampleInfo(info);
      setWindowSize(info.window_size);

      // Notify parent component that samples have changed
      if (onSamplesChanged) {
        await onSamplesChanged(info);
      }
    } catch (error) {
      console.error(`Error during ${action}:`, error);
      alert(`Failed to ${action}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWindowSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setWindowSize(newSize);
    handleNavigation("setWindow", { windowSize: newSize });
  };

  const handleJumpToFirst = () => {
    const sample_high = Math.min(windowSize, sampleInfo.total_samples);
    handleNavigation("navigate", { sample_low: 1, sample_high });
  };

  const handleJumpToLast = () => {
    const sample_low = Math.max(1, sampleInfo.total_samples - windowSize + 1);
    const sample_high = sampleInfo.total_samples;
    handleNavigation("navigate", { sample_low, sample_high });
  };

  // Simple fixed window size options
  const windowSizeOptions = [3, 5, 7];

  // Calculate current page for display

  return (
    <div className="sample-navigator ag-grid-toolbar">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center ag-pagination-panel">
          {/* Navigation Buttons */}
          <div className="ag-grid-btn-group">
            <button
              type="button"
              className="ag-grid-btn btn-animate"
              onClick={handleJumpToFirst}
              disabled={isLoading || sampleInfo.current_sample_low === 1}
              title="Jump to first samples"
            >
              &#171;
            </button>

            <button
              type="button"
              className="ag-grid-btn btn-animate"
              onClick={() => handleNavigation("previous")}
              disabled={isLoading || !sampleInfo.can_go_previous}
              title="Previous samples"
            >
              &#8249;
            </button>

            <span className="ag-pagination-info">
              Samples{" "}
              <strong>
                {sampleInfo.current_sample_low}-{sampleInfo.current_sample_high}
              </strong>{" "}
              of <strong>{sampleInfo.total_samples}</strong>
            </span>

            <button
              type="button"
              className="ag-grid-btn btn-animate"
              onClick={() => handleNavigation("next")}
              disabled={isLoading || !sampleInfo.can_go_next}
              title="Next samples"
            >
              &#8250;
            </button>

            <button
              type="button"
              className="ag-grid-btn btn-animate"
              onClick={handleJumpToLast}
              disabled={
                isLoading ||
                sampleInfo.current_sample_high === sampleInfo.total_samples
              }
              title="Jump to last samples"
            >
              &#187;
            </button>
          </div>
        </div>

        {/* Current file indicator - hidden on mobile */}
        {sampleInfo.current_file && (
          <span className="ag-pagination-info mx-3 d-none d-md-inline" style={{ color: "var(--bs-secondary-color)", fontSize: "0.875rem", fontWeight: "bold" }}>
            {sampleInfo.current_file}
          </span>
        )}

        <div className="d-flex align-items-center">
          <span className="ag-grid-label d-none d-md-inline">Window Size:</span>
          <select
            className="ag-grid-select"
            value={windowSize}
            onChange={handleWindowSizeChange}
            disabled={isLoading}
            title="Window Size"
          >
            {windowSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {isLoading && (
            <div className="ms-2">
              <LoadingSpinner size="0.8rem" className="text-secondary" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SampleNavigator;
